<template>
  <v-autocomplete
    :model-value="inputValue"
    :multiple="multiple"
    :closable-chips="multiple"
    :clearable="isClearable"
    clear-icon="mdi-close"
    variant="outlined"
    hide-details
    :menu-props="{ maxWidth: '365px' }"
    auto-select-first
    :label="inputLabel"
    placeholder="Select or type to search"
    menu-icon="mdi-plus-circle-outline"
    :items="sortedItem"
    item-title="text"
    class="autocomplete-input"
    @update:modelValue="onValueChange"
    @update:search="onSearchInput"
    @update:focused="handleFocus"
    ref="autocomplete"
    color="rgb(var(--v-theme-info))"
    :class="{ focused: isAutoCompleteFocused }"
  >
    <template v-slot:prepend-item v-if="isSlotPrependItem">
      <v-row
        style="
          height: 50px;
          align-items: center;
          justify-content: space-between;
          padding-left: 10%;
          padding-right: 10%;
        "
      >
        <v-btn
          :disabled="loading"
          class="ma-1"
          color="primary"
          variant="plain"
          style="font-weight: bold"
          @click="handleSelectAll"
          id="autocomplete-select-all"
        >
          Select All
        </v-btn>
        <v-btn
          :disabled="loading"
          class="ma-1"
          color="primary"
          variant="plain"
          style="font-weight: bold"
          @click="deselectAll"
          id="autocomplete-delete-all"
        >
          Deselect All
        </v-btn>
      </v-row>
      <v-divider class="mt-2"></v-divider>
    </template>
    <!-- eslint-disable-next-line -->
    <template v-if="multiple" v-slot:selection="{ item, index }">
      <v-chip
        v-if="isChipVisible(index)"
        closable
        size="small"
        close-icon="mdi-close"
        @click:close="handleDelete(item)"
        label
        id="autocomplete-chip"
      >
        <span class="autocomplete-chip-text">{{ item.title }}</span>
      </v-chip>

      <span
        id="autocomplete-other-selection-text"
        v-if="index === numberOfSelectedItemsToDisplay"
        class="text-grey text-caption"
      >
        (+{{ inputValue.length - numberOfSelectedItemsToDisplay }} others)
      </span>
    </template>

    <template v-if="hasTooltip" v-slot:item="{ props: autocompleteProps, item }">
      <v-tooltip location="bottom">
        <template v-slot:activator="{ props: tooltipProps }">
          <v-list-item :value="item.title" v-bind="mergeProps(autocompleteProps, tooltipProps)">
            <template v-slot:prepend="{ isActive }">
              <v-list-item-action v-if="multiple" start>
                <v-checkbox-btn
                  :model-value="isActive"
                  color="rgb(var(--v-theme-info))"
                ></v-checkbox-btn>
              </v-list-item-action>
            </template>
          </v-list-item>
        </template>
        <span>{{ item.title }}</span>
      </v-tooltip>
    </template>
  </v-autocomplete>
</template>

<script>
import { CSBase } from '@complispace/cs-design-system';
import uniq from 'lodash/uniq';
import { mergeProps } from 'vue';

export default {
  name: 'ContextKeyFilter',
  extends: CSBase,

  components: {},

  computed: {
    sortedItem() {
      return this.sortItemList(this.items);
    }
  },

  data() {
    return {
      localFilteredItems: [],
      hasSearchValue: null,
      isAutoCompleteFocused: false
    };
  },

  props: {
    inputLabel: {
      type: String,
      default: '',
      required: true
    },
    items: {
      type: Array,
      required: true
    },
    inputValue: {
      type: [Array, Boolean, Number, String]
    },
    onValueChange: {
      type: Function,
      required: true
    },
    numberOfSelectedItemsToDisplay: {
      type: Number
    },
    isSlotPrependItem: {
      type: Boolean,
      default: false
    },
    multiple: {
      type: Boolean,
      default: true
    },
    isClearable: {
      type: Boolean,
      default: true
    },
    hasTooltip: {
      type: Boolean,
      default: true
    }
  },
  methods: {
    mergeProps,
    handleFocus(focusState) {
      // Explain on why Focus - Select All & Deselect All somehow will trigger onBlur. So i use focus. When i click out, the textbox will be cleared as well, i don handle on blur but when i onFocus. The text is empty, so i out everything in the filterMenu again.
      this.localFilteredItems = this.items;
      this.isAutoCompleteFocused = focusState;
    },
    onSearchInput(value) {
      this.hasSearchValue = value;
      this.$nextTick(() => {
        this.localFilteredItems = [...this.$refs.autocomplete.filteredItems];
      });
    },
    handleKeydown(event) {
      if (event.key === 'Escape' || event.key === 'Esc') {
        this.localFilteredItems = [...this.items];
      }
    },
    handleSelectAll() {
      if (!this.multiple) return;

      if (this.localFilteredItems.length === 0 && !this.hasSearchValue) {
        const allItems = this.items.map((item) => item.value);

        const allUniqItems = uniq(allItems);
        this.onValueChange(allUniqItems);
      } else {
        const updateSelectedItem = [
          ...this.inputValue,
          ...this.localFilteredItems.map((item) => item.value)
        ];

        const uniqItems = uniq(updateSelectedItem);

        this.onValueChange(uniqItems);
      }
    },
    deselectAll() {
      if (!this.multiple) return;

      let itemDeselected;

      if (this.localFilteredItems.length === 0 && !this.hasSearchValue) {
        itemDeselected = [];
      } else {
        const localFilteredItemValue = this.localFilteredItems.map((item) => item.value);

        itemDeselected = this.inputValue.filter((item) => !localFilteredItemValue.includes(item));
      }

      this.onValueChange(itemDeselected);
    },
    handleDelete(item) {
      if (!this.multiple) return;

      const newInputValue = this.inputValue.filter((i) => i !== item.value);
      this.onValueChange(newInputValue);
    },
    isChipVisible(index) {
      return (
        this.numberOfSelectedItemsToDisplay === undefined ||
        index < this.numberOfSelectedItemsToDisplay
      );
    },
    sortItemList(items) {
      return items.slice().sort((a, b) => {
        const nameA = a.text.toString().toLowerCase();
        const nameB = b.text.toString().toLowerCase();

        // Compare alphabetically and numerically
        if (nameA < nameB) return -1;
        if (nameA > nameB) return 1;
        return 0;
      });
    }
  }
};
</script>

<style scoped>
.autocomplete-input {
  margin: 0;
  padding: 0;
}
.autocomplete-chip-text {
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.v-autocomplete--active-menu.autocomplete-input :deep(.v-icon--size-default) {
  color: rgb(var(--v-theme-info)) !important;
  opacity: 1;
}
.autocomplete-input.focused :deep(.v-icon--size-default) {
  color: rgb(var(--v-theme-info)) !important;
  opacity: 1;
}
</style>
